import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowSquareOutIcon, UserPlusIcon, ClipboardTextIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import LinkedSection from "../../components/crm/LinkedSection";
import InlineEditableField from "../../components/crm/InlineEditableField";
import CopyButton from "../../components/CopyButton";
import {
  ConfirmDelete,
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
} from "../../components/crm/Form";
import { FieldCard } from "../crm/CustomerDetail";
import StatusBadge from "../../components/ops/StatusBadge";
import StatusActionBar from "../../components/ops/StatusActionBar";
import StatusTimeline from "../../components/ops/StatusTimeline";
import ChecklistEditor from "../../components/ops/ChecklistEditor";
import NotesPanel from "../../components/ops/NotesPanel";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { REPORT_STATUS_LABEL, REPORT_STATUS_TINT } from "../../lib/diagnostics";

const OFFICE = ["owner", "ops"];
const STATUSES = [
  "draft",
  "scheduled",
  "assigned",
  "en_route",
  "on_site",
  "paused",
  "completed",
  "canceled",
];
const optsFrom = (arr) =>
  arr.map((v) => ({ value: v, label: v.replace(/_/g, " ") }));

export default function WorkOrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const [doc, setDoc] = useState(null);
  const [report, setReport] = useState(null);  // Phase 5 — singleton diagnostic report
  const [activeAckCount, setActiveAckCount] = useState(0);  // Phase 5c
  const [latestAckAt, setLatestAckAt] = useState(null);  // Phase 5c
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const isOffice = OFFICE.includes(user?.role);
  const isLeadAssigned = doc && doc.assigned_lead_tech_user_id === user?.id;
  const isAssistAssigned =
    doc && doc.assigned_assistant_user_id === user?.id;
  const isAssigned = isLeadAssigned || isAssistAssigned;
  const isTech = user?.role === "lead_tech" || user?.role === "tech_assistant";

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/work-orders/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
    // Phase 5 — best-effort fetch of the singleton diagnostic report.
    try {
      const r = await api.get(`/work-orders/${id}/diagnostic-report`);
      setReport(r.data);
      // Phase 5c — best-effort fetch of acknowledgment count for the badge.
      if (r.data?.id && r.data?.status === "finalized") {
        try {
          const ackResp = await api.get(
            `/diagnostic-reports/${r.data.id}/acknowledgments`
          );
          const list = ackResp.data || [];
          // Active = non-revoked AND not stale (acknowledged on the
          // current finalized version of the report).
          const active = list.filter((a) => !a.revoked_at && !a.is_stale);
          setActiveAckCount(active.length);
          setLatestAckAt(active[0]?.created_at || null);
        } catch {
          setActiveAckCount(0);
          setLatestAckAt(null);
        }
      } else {
        setActiveAckCount(0);
        setLatestAckAt(null);
      }
    } catch (e2) {
      if (e2?.response?.status === 404) {
        setReport(null);
      }
      // Other errors (403 for unassigned tech) silently leave report=null.
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/work-orders/${id}`);
      nav("/work-orders");
    } catch (e) {
      setErr(formatApiError(e));
      setDelBusy(false);
    }
  };

  if (!doc)
    return (
      <Layout title="Work Order">
        <ErrorBanner message={err} />
      </Layout>
    );

  // Field permissions:
  //   isOffice → can edit any field via Edit modal
  //   isLeadAssigned → can edit field_summary + resolution_summary
  //   isAssistAssigned → cannot edit any free-text fields here
  const canEditAnything = isOffice || isLeadAssigned;

  return (
    <Layout
      title={doc.property_address || "Work Order"}
      subtitle="Work order"
    >
      <EntityHeader
        overline="work order"
        title={doc.property_address || "Work Order"}
        breadcrumb={[
          {
            label: isTech ? "My Work" : "Work Orders",
            to: isTech ? "/my-work" : "/work-orders",
          },
          { label: doc.id.slice(0, 8) },
        ]}
        meta={
          <>
            <StatusBadge value={doc.status} />
            <span className="inline-flex items-center gap-1.5">
              id · WO-{doc.id.slice(-8).toUpperCase()}
              <CopyButton
                value={doc.id}
                label="Work Order ID"
                testId="wo-copy-id"
              />
            </span>
            {doc.scheduled_start && (
              <span>
                scheduled · {new Date(doc.scheduled_start).toLocaleString()}
              </span>
            )}
            {doc.assigned_lead_name && (
              <span>lead · {doc.assigned_lead_name}</span>
            )}
            {doc.assigned_assistant_name && (
              <span>asst · {doc.assigned_assistant_name}</span>
            )}
          </>
        }
        canEdit={canEditAnything}
        canDelete={isOffice}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDelOpen(true)}
      />

      <ErrorBanner message={err} />

      {/* Field action bar — visible only to assigned techs (forward transitions) */}
      <div className="mb-6">
        <StatusActionBar
          wo={doc}
          isAssigned={isAssigned}
          onChange={load}
        />
        {isOffice && (
          <div className="mt-3 flex flex-wrap gap-2">
            <SecondaryButton
              onClick={() => setStatusOpen(true)}
              data-testid="wo-office-status-button"
            >
              Change Status (Office)
            </SecondaryButton>
            <SecondaryButton
              onClick={() => setAssignOpen(true)}
              data-testid="wo-assign-button"
            >
              <UserPlusIcon size={14} weight="bold" /> Assign Tech
            </SecondaryButton>
          </div>
        )}
      </div>

      {doc.service_request_id && (
        <div className="mb-6 bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between gap-2">
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
            // sourced from service request
          </div>
          {isOffice && (
            <Link
              to={`/service-requests/${doc.service_request_id}`}
              data-testid="wo-sr-link"
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-amber-700"
            >
              View Request <ArrowSquareOutIcon size={12} weight="bold" />
            </Link>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <FieldCard label="Customer" value={doc.customer_name || "—"} />
        <FieldCard label="Property" value={doc.property_address || "—"} />
        <FieldCard
          label="Closed At"
          value={doc.closed_at ? new Date(doc.closed_at).toLocaleString() : "—"}
          mono
        />
        <InlineEditableField
          label="Dispatch Notes"
          value={doc.dispatch_notes}
          name="dispatch_notes"
          as="textarea"
          canEdit={isOffice}
          endpoint={`/work-orders/${id}`}
          placeholder="Add dispatch notes"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="wo-inline"
          className="sm:col-span-2 lg:col-span-3"
        />
        <InlineEditableField
          label="Field Summary"
          value={doc.field_summary}
          name="field_summary"
          as="textarea"
          canEdit={isOffice || isLeadAssigned}
          endpoint={`/work-orders/${id}`}
          placeholder="What did the tech find on site?"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="wo-inline"
          className="sm:col-span-2 lg:col-span-3"
        />
        <InlineEditableField
          label="Resolution Summary"
          value={doc.resolution_summary}
          name="resolution_summary"
          as="textarea"
          canEdit={isOffice || isLeadAssigned}
          endpoint={`/work-orders/${id}`}
          placeholder="What was resolved / next steps?"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="wo-inline"
          className="sm:col-span-2 lg:col-span-3"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <LinkedSection
            title="Checklist"
            subtitle="completion items for this job"
            count={doc.checklist.length}
            data-testid="wo-checklist-section"
          >
            <div className="p-4">
              <ChecklistEditor
                workOrderId={doc.id}
                items={doc.checklist}
                canManage={isOffice}
                canToggle={isOffice || isAssigned}
                onChange={load}
              />
            </div>
          </LinkedSection>

          {/* Phase 5 — Diagnostic Report summary panel + CTA */}
          <section
            data-testid="wo-diagnostic-section"
            className="bg-white border border-slate-200 rounded-md p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-heading text-sm font-semibold text-slate-900">
                  Diagnostic Report
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Findings · recommendations · parts · photos
                </div>
              </div>
              <Link
                to={`/work-orders/${doc.id}/report`}
                data-testid="wo-open-report"
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-amber-700 whitespace-nowrap"
              >
                <ClipboardTextIcon size={14} weight="duotone" />
                {report ? "Open report" : "Start report"}
                <ArrowSquareOutIcon size={12} weight="bold" />
              </Link>
            </div>
            {report && (
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                <span
                  data-testid={`wo-report-status-${report.status}`}
                  className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    REPORT_STATUS_TINT[report.status] ||
                    "bg-slate-100 text-slate-700 border-slate-300"
                  }`}
                >
                  {REPORT_STATUS_LABEL[report.status] || report.status}
                </span>
                <span className="text-slate-600">
                  {report.photo_count}{" "}
                  {report.photo_count === 1 ? "photo" : "photos"}
                </span>
                {report.submitted_by_name && (
                  <span className="text-slate-500">
                    · submitted by {report.submitted_by_name}
                  </span>
                )}
                {report.finalized_by_name && (
                  <span className="text-slate-500">
                    · finalized by {report.finalized_by_name}
                  </span>
                )}
                {report.status === "finalized" && activeAckCount > 0 && (
                  <span
                    data-testid="wo-owner-acknowledged-pill"
                    title={
                      latestAckAt
                        ? `Acknowledged ${new Date(latestAckAt).toLocaleString()}`
                        : "Owner acknowledged"
                    }
                    className="inline-flex items-center rounded-md border bg-emerald-100 text-emerald-900 border-emerald-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                  >
                    Owner acknowledged
                    {activeAckCount > 1 ? ` ×${activeAckCount}` : ""}
                  </span>
                )}
              </div>
            )}
            {!report && (
              <div className="mt-3 text-xs text-slate-500">
                No report yet — click <em>Start report</em> to begin.
              </div>
            )}
          </section>

          <LinkedSection
            title="Notes"
            subtitle={
              isTech
                ? "field + customer-visible notes (internal hidden)"
                : "internal · field · customer-visible"
            }
            count={doc.notes.length}
            data-testid="wo-notes-section"
          >
            <div className="p-4">
              <NotesPanel
                workOrderId={doc.id}
                notes={doc.notes}
                canCreate={isOffice || isAssigned}
                canCreateInternal={isOffice}
                canDelete={isOffice}
                onChange={load}
              />
            </div>
          </LinkedSection>
        </div>

        <aside className="space-y-6">
          <section
            className="bg-white border border-slate-200 rounded-sm p-4"
            data-testid="wo-status-log-section"
          >
            <div className="font-heading text-sm font-semibold text-slate-900">
              Status History
            </div>
            <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1 mb-3">
              // every transition
            </div>
            <StatusTimeline entries={doc.status_log} />
          </section>

          <section
            className="bg-white border border-slate-200 rounded-sm p-4"
            data-testid="wo-assignments-section"
          >
            <div className="font-heading text-sm font-semibold text-slate-900">
              Assignment History
            </div>
            <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1 mb-3">
              // append-only
            </div>
            {doc.assignments.length === 0 ? (
              <div className="text-xs text-slate-500">No assignments yet.</div>
            ) : (
              <ol className="space-y-2 text-sm">
                {doc.assignments.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-start gap-2"
                    data-testid={`assignment-${a.id}`}
                  >
                    <UserPlusIcon
                      size={14}
                      weight="duotone"
                      className="mt-0.5 text-slate-400"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-900">{a.user_name || a.user_id.slice(0, 8)}</div>
                      <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
                        {a.role_on_job.replace("_", " ")} ·{" "}
                        {new Date(a.assigned_at).toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </aside>
      </div>

      {canEditAnything && (
        <EditWorkOrderModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          wo={doc}
          isOffice={isOffice}
          isLeadAssigned={isLeadAssigned}
          onSaved={(d) => {
            setEditOpen(false);
            setDoc({ ...doc, ...d });
          }}
        />
      )}
      {isOffice && (
        <>
          <OfficeStatusModal
            open={statusOpen}
            onClose={() => setStatusOpen(false)}
            wo={doc}
            onSaved={() => {
              setStatusOpen(false);
              load();
            }}
          />
          <AssignTechModal
            open={assignOpen}
            onClose={() => setAssignOpen(false)}
            wo={doc}
            onSaved={() => {
              setAssignOpen(false);
              load();
            }}
          />
          <ConfirmDelete
            open={delOpen}
            onClose={() => setDelOpen(false)}
            onConfirm={handleDelete}
            label="Work Order"
            busy={delBusy}
          />
        </>
      )}
    </Layout>
  );
}

function EditWorkOrderModal({
  open,
  onClose,
  wo,
  isOffice,
  isLeadAssigned,
  onSaved,
}) {
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        scheduled_start: wo.scheduled_start ? wo.scheduled_start.slice(0, 16) : "",
        scheduled_end: wo.scheduled_end ? wo.scheduled_end.slice(0, 16) : "",
        dispatch_notes: wo.dispatch_notes || "",
        field_summary: wo.field_summary || "",
        resolution_summary: wo.resolution_summary || "",
      });
      setErr("");
    }
  }, [open, wo]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {};
      if (isOffice) {
        payload.scheduled_start = form.scheduled_start || null;
        payload.scheduled_end = form.scheduled_end || null;
        payload.dispatch_notes = form.dispatch_notes || null;
      }
      if (isOffice || isLeadAssigned) {
        payload.field_summary = form.field_summary || null;
        payload.resolution_summary = form.resolution_summary || null;
      }
      const { data } = await api.patch(`/work-orders/${wo.id}`, payload);
      onSaved(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Work Order"
      subtitle="update fields"
      data-testid="wo-edit-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        {isOffice && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Scheduled Start">
                <input
                  type="datetime-local"
                  value={form.scheduled_start || ""}
                  onChange={(e) =>
                    setForm({ ...form, scheduled_start: e.target.value })
                  }
                  data-testid="wo-edit-start"
                  className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                />
              </Field>
              <Field label="Scheduled End">
                <input
                  type="datetime-local"
                  value={form.scheduled_end || ""}
                  onChange={(e) =>
                    setForm({ ...form, scheduled_end: e.target.value })
                  }
                  data-testid="wo-edit-end"
                  className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                />
              </Field>
            </div>
            <Field label="Dispatch Notes">
              <TextArea
                value={form.dispatch_notes || ""}
                onChange={(v) => setForm({ ...form, dispatch_notes: v })}
                data-testid="wo-edit-dispatch-notes"
              />
            </Field>
          </>
        )}
        {(isOffice || isLeadAssigned) && (
          <>
            <Field label="Field Summary">
              <TextArea
                value={form.field_summary || ""}
                onChange={(v) => setForm({ ...form, field_summary: v })}
                rows={3}
                data-testid="wo-edit-field-summary"
              />
            </Field>
            <Field label="Resolution Summary">
              <TextArea
                value={form.resolution_summary || ""}
                onChange={(v) => setForm({ ...form, resolution_summary: v })}
                rows={3}
                data-testid="wo-edit-resolution-summary"
              />
            </Field>
          </>
        )}
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="wo-edit-submit"
          >
            {busy ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function OfficeStatusModal({ open, onClose, wo, onSaved }) {
  const [target, setTarget] = useState(wo.status);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTarget(wo.status);
      setNote("");
      setErr("");
    }
  }, [open, wo]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post(`/work-orders/${wo.id}/status`, {
        status: target,
        note: note || null,
      });
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change Status"
      subtitle="office override"
      data-testid="wo-office-status-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Target Status"
          required
          hint="Office can move to any status, including reopening completed jobs."
        >
          <Select
            value={target}
            onChange={(v) => setTarget(v || wo.status)}
            options={optsFrom(STATUSES)}
            placeholder=""
            data-testid="wo-office-status-target"
          />
        </Field>
        <Field label="Note">
          <TextArea
            value={note}
            onChange={setNote}
            rows={3}
            data-testid="wo-office-status-note"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="wo-office-status-submit"
          >
            {busy ? "Saving…" : "Apply"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function AssignTechModal({ open, onClose, wo, onSaved }) {
  const [techs, setTechs] = useState([]);
  const [role, setRole] = useState("lead_tech");
  const [userId, setUserId] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRole("lead_tech");
    setUserId(wo.assigned_lead_tech_user_id || "");
    setErr("");
    api
      .get("/dispatch/board")
      .then((r) => setTechs(r.data.techs || []))
      .catch(() => {});
  }, [open, wo]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post(`/work-orders/${wo.id}/assign`, {
        role_on_job: role,
        user_id: userId || null,
      });
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign Technician"
      subtitle="lead or assistant"
      data-testid="wo-assign-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Role on Job" required>
          <Select
            value={role}
            onChange={(v) => {
              const r = v || "lead_tech";
              setRole(r);
              setUserId(
                r === "lead_tech"
                  ? wo.assigned_lead_tech_user_id || ""
                  : wo.assigned_assistant_user_id || ""
              );
            }}
            options={[
              { value: "lead_tech", label: "Lead Tech" },
              { value: "tech_assistant", label: "Tech Assistant" },
            ]}
            placeholder=""
            data-testid="wo-assign-role"
          />
        </Field>
        <Field label="Technician">
          <Select
            value={userId}
            onChange={(v) => setUserId(v || "")}
            options={techs.map((t) => ({
              value: t.id,
              label: `${t.name} · ${t.role}`,
            }))}
            placeholder="(unassign)"
            data-testid="wo-assign-user"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="wo-assign-submit"
          >
            {busy ? "Saving…" : "Save Assignment"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
